import queryString from 'query-string';
import { Moment } from 'moment';
import {
  ClientExtendedReportParams,
  ClientFilter,
  ClientInfoData,
  ClientReportParams,
  ClientTag,
  IClientsApiCall,
  IRestApiService,
  MessageBatchParams,
  MessageParams,
  RatePlanDiscount,
  SpreadsheetMapping,
  StudioClient,
  UnavailableSlotData,
  UploadDocParams,
  User,
  MessageEvent,
  GetAllMessagesParams,
} from '../types';
import { ClientTracker } from '../services/analytics/apiTrackers';

class ClientsApiCall implements IClientsApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  addMessanger(ownerId: string, clientId: string, params: any): Promise<any> {
    const url = `clients/${ownerId}/${clientId}/add-messanger`;
    return this.restApiService.post(url, params);
  }

  getMyClients(ownerId: string, noOnHold?: boolean, onHoldDate?: string, studioId?: string): Promise<StudioClient[]> {
    let url = `clients/${ownerId}?`;
    if (noOnHold && onHoldDate) {
      url += `noOnHold=true&onHoldDate=${onHoldDate}&`;
    }
    if (studioId) {
      url += `studioId=${studioId}&`;
    }

    return this.restApiService.get(url);
  }

  getVisitsByRetentionMonthlyDashboard(
    ownerId: string,
    studioId: string,
    from: Moment,
    to: Moment,
    clientTags?: string[]
  ): Promise<any> {
    let url = `clients/${ownerId}/${studioId}/dashboard/visits-retention-monthly?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;

    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }

    return this.restApiService.get(url);
  }

  getDeletedClientsMonthlyDashboard(
    ownerId: string,
    studioId: string,
    from: Moment,
    to: Moment,
    clientTags?: string[]
  ): Promise<any> {
    let url = `clients/${ownerId}/${studioId}/dashboard/deleted-clients-monthly?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;

    if (clientTags) {
      url += `&${queryString.stringify({ clientTags })}`;
    }

    return this.restApiService.get(url);
  }

  addClassTag(ownerId: string, clientIds: string[], tag: string, clientId?: string): Promise<any> {
    const url = `clients/${ownerId}/add-tags`;

    return this.restApiService.post(url, {
      clientIds,
      clientTag: tag,
      clientId,
    });
  }

  updateClientTags(ownerId: string, clientId: string, tags: ClientTag[]): Promise<any> {
    const url = `clients/${ownerId}/${clientId}/tags`;
    const body = { tags };
    return this.restApiService.post(url, body);
  }

  removeClientTags(
    ownerId: string,
    clientId: string,
    tags: ClientTag[],
    course?: any,
    studioId?: string
  ): Promise<any> {
    const url = `clients/${ownerId}/${clientId}/tags/remove`;
    return this.restApiService.post(url, { tags, course, studioId });
  }

  addClientTags(ownerId: string, clientId: string, tags: ClientTag[]): Promise<any> {
    const url = `clients/${ownerId}/${clientId}/tags/add`;
    return this.restApiService.post(url, { tags });
  }

  findClientsByFiltersWithBalance(
    ownerId: string,
    filter: ClientFilter,
    showDeleted: boolean
  ): Promise<StudioClient[]> {
    const url = `clients/${ownerId}/find-clients`;

    return this.restApiService.post(url, {
      filter,
      showDeleted,
      includeBalance: true,
    });
  }

  startClientsExport(
    ownerId: string,
    filter: ClientFilter,
    showDeleted: boolean
  ): Promise<{ jobId: string; downloadUrl: string }> {
    const url = `clients/${ownerId}/find-clients/export`;
    return this.restApiService.post(url, { filter, showDeleted });
  }

  getActiveClients(
    ownerId: string,
    params?: { customProjection?: Record<keyof Client, 1 | 0> }
  ): Promise<StudioClient[]> {
    return this.restApiService.post(`clients/${ownerId}/active-clients`, params);
  }

  getMyDeletedClients(ownerId: string): Promise<StudioClient[]> {
    const url = `clients/${ownerId}/deleted`;
    return this.restApiService.get(url);
  }

  getBirthdays(ownerId: string): Promise<any> {
    const url = `clients/${ownerId}/birthdays`;
    return this.restApiService.get(url);
  }

  getStudioClientsByPhone(): Promise<any> {
    const url = `clients/clients-by-phone`;
    return this.restApiService.get(url);
  }

  getAllStudioClientsByPhone(phone: string): Promise<any> {
    const url = `clients/all-clients-by-phone`;
    return this.restApiService.post(url, { phone });
  }

  checkPhone(ownerId: string, phone: string, excludeClientId?: string): Promise<any> {
    const url = `clients/${ownerId}/check-phone`;
    return this.restApiService.post(url, {
      phone,
      excludeClientId,
    });
  }

  checkPhoneOnSignUp(ownerId: string, phone: string, excludeClientId?: string): Promise<any> {
    const url = `clients/${ownerId}/check-phone-sign-up`;
    return this.restApiService.post(url, {
      phone,
      excludeClientId,
    });
  }

  checkPhonePin(ownerId: string, phone: string, pin: string): Promise<any> {
    const url = `clients/${ownerId}/check-phone-pin`;
    return this.restApiService.post(url, { phone, pin });
  }

  getMyClient(ownerId: string, clientId: string): Promise<StudioClient> {
    return this.restApiService.get(`clients/${ownerId}/${clientId}`);
  }

  updateMyClientRatePlanDiscounts(ownerId: string, clientId: string, discounts: RatePlanDiscount[]): Promise<any> {
    return this.restApiService.post(`clients/${ownerId}/${clientId}/discounts`, { discounts });
  }

  getClientByPhone(ownerId: string, studioId: string, groupId: string, phone: string): Promise<StudioClient> {
    return this.restApiService.get(`clients/${ownerId}/phone/${studioId}/${groupId}/${phone}`);
  }

  getMyVooptyClient(ownerId: string, vooptyClientId: string): Promise<any> {
    return this.restApiService.get(`clients/${ownerId}/voopty/qr/${vooptyClientId}`);
  }

  getTags(ownerId: string): Promise<ClientTag[]> {
    return this.restApiService.get(`clients/${ownerId}/tags`);
  }

  countClassTags(ownerId: string, tags: string[]): Promise<any> {
    const body = { tags };
    return this.restApiService.post(`clients/${ownerId}/tags/count`, body);
  }

  renameClassTag(ownerId: string, currentName: string, newName: string): Promise<any> {
    const body = { currentName, newName };
    return this.restApiService.post(`clients/${ownerId}/tags/rename`, body);
  }

  deleteTagForClients(ownerId: string, tagName: string): Promise<any> {
    return this.restApiService.delete(`clients/${ownerId}/tags/${tagName}/remove`);
  }

  getMyClientReport(ownerId: string, clientId: string, params: ClientReportParams): Promise<any> {
    const { from, to, sort, excludeCanceled } = params;
    const queryParams = {
      from: from.format('DD-MM-YYYY'),
      to: to.format('DD-MM-YYYY'),
      sort,
      excludeCanceled,
    };

    const url = `clients/${ownerId}/${clientId}/report`;
    const urlParams = new URLSearchParams(
      Object.entries(queryParams).filter(([_, value]) => value !== undefined) as [string, string][]
    );

    return this.restApiService.get(`${url}?${urlParams.toString()}`);
  }

  getMyClientExtendedReport(ownerId: string, clientId: string, params: ClientExtendedReportParams): Promise<any> {
    const { from, to, sort, studioIds, excludeCanceled } = params;
    const queryParams = {
      from: from.format('DD-MM-YYYY'),
      to: to.format('DD-MM-YYYY'),
    };

    const body = {
      studioIds,
      sort,
      excludeCanceled: excludeCanceled || false,
    };

    const url = `clients/${ownerId}/${clientId}/report`;
    const urlSearchParams = new URLSearchParams(
      Object.entries(queryParams).filter(([_, value]) => value !== undefined) as [string, string][]
    );

    return this.restApiService.post(`${url}?${urlSearchParams.toString()}`, body);
  }

  addClient(ownerId: string, client: Partial<StudioClient>): Promise<StudioClient> {
    const startTime = Date.now();

    return this.restApiService
      .post(`clients/${ownerId}`, { client })
      .then((result) => {
        ClientTracker.trackAddClient({
          ownerId,
          client,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackAddClient({
          ownerId,
          client,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  updateClient(ownerId: string, id: string, client: Partial<StudioClient>): Promise<StudioClient> {
    const startTime = Date.now();

    return this.restApiService
      .put(`clients/${ownerId}/${id}`, { client })
      .then((result) => {
        ClientTracker.trackUpdateClient({
          ownerId,
          id,
          client,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackUpdateClient({
          ownerId,
          id,
          client,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteClient(ownerId: string, client: StudioClient): Promise<any> {
    const startTime = Date.now();
    const clientId = client._id;

    return this.restApiService
      .delete(`clients/${ownerId}/${clientId}`)
      .then((result) => {
        ClientTracker.trackDeleteClient({
          ownerId,
          client,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackDeleteClient({
          ownerId,
          client,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteClientForever(ownerId: string, clientId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .delete(`clients/delete-forever/${ownerId}/${clientId}`)
      .then((result) => {
        ClientTracker.trackDeleteClientForever({
          ownerId,
          clientId,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackDeleteClientForever({
          ownerId,
          clientId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  unDeleteClient(ownerId: string, client: StudioClient): Promise<any> {
    const startTime = Date.now();
    const clientId = client._id;

    return this.restApiService
      .delete(`clients/${ownerId}/${clientId}/undelete`)
      .then((result) => {
        ClientTracker.trackUnDeleteClient({
          ownerId,
          client,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackUnDeleteClient({
          ownerId,
          client,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteClientsOnHold(ownerId: string, clientId: string): Promise<any> {
    return this.restApiService.delete(`clients/${ownerId}/${clientId}/onHold`);
  }

  bulkDeleteClients(ownerId: string, ids: string[]): Promise<any> {
    return this.restApiService.post(`clients/${ownerId}/bulk/delete`, { ids });
  }

  bulkUndeleteClients(ownerId: string, ids: string[]): Promise<any> {
    return this.restApiService.post(`clients/${ownerId}/bulk/undelete`, { ids });
  }

  bulkDeleteClientsForever(ownerId: string, ids: string[]): Promise<any> {
    return this.restApiService.post(`clients/${ownerId}/bulk/delete-forever`, { ids });
  }

  ping(ownerId: string, client: StudioClient): Promise<any> {
    const clientId = client._id;
    return this.restApiService.post(`clients/${ownerId}/${clientId}/ping`);
  }

  generatePin(ownerId: string, clientId: string): Promise<any> {
    return this.restApiService.post(`clients/${ownerId}/${clientId}/generate-pin`);
  }

  connect(ownerId: string, studioClientId: string, userId: string): Promise<any> {
    return this.restApiService.post(`clients/${ownerId}/connect/${studioClientId}/${userId}`);
  }

  extendRatePlan(ownerId: string, studioClientId: string, data: any): Promise<any> {
    return this.restApiService.post(`clients/${ownerId}/${studioClientId}/ratePlan`, data);
  }

  deleteRatePlan(ownerId: string, studioClientId: string, id: string): Promise<any> {
    return this.restApiService.delete(`clients/${ownerId}/${studioClientId}/ratePlan/${id}`);
  }

  setUnavailable(ownerId: string, id: string, data: UnavailableSlotData): Promise<any> {
    const url = `clients/${ownerId}/${id}/unavailable`;
    return this.restApiService.put(url, data);
  }

  resetUnavailableSlot(ownerId: string, id: string, data: UnavailableSlotData): Promise<any> {
    const url = `clients/${ownerId}/${id}/available`;
    return this.restApiService.put(url, data);
  }

  getStudioClientMessages(ownerId: string, studioClientId: string): Promise<any> {
    const url = `messages/${ownerId}/${studioClientId}`;
    return this.restApiService.get(url);
  }

  getAllMessages(
    ownerId: string,
    params: GetAllMessagesParams = {}
  ): Promise<{ items: MessageEvent[]; total: number }> {
    const queryParams: any = {};
    if (params.types?.length) queryParams.types = params.types;
    if (params.statuses?.length) queryParams.statuses = params.statuses;
    if (params.role) queryParams.role = params.role;
    if (params.from) queryParams.from = params.from;
    if (params.to) queryParams.to = params.to;
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.limit !== undefined) queryParams.limit = params.limit;

    const url = `messages/${ownerId}?${queryString.stringify(queryParams)}`;
    return this.restApiService.get(url);
  }

  createMessageBatch(ownerId: string, params: MessageBatchParams): Promise<any> {
    const startTime = Date.now();
    const url = `messages/${ownerId}/create-batch`;

    return this.restApiService
      .post(url, params)
      .then((result) => {
        ClientTracker.trackCreateMessageBatch({
          ownerId,
          params,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackCreateMessageBatch({
          ownerId,
          params,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getMessageBatch(ownerId: string, id: string): Promise<any> {
    const url = `messages/${ownerId}/get-batch/${id}`;
    return this.restApiService.get(url);
  }

  getMessagesFromBatch(ownerId: string, id: string): Promise<any> {
    const url = `messages/${ownerId}/batch/${id}`;
    return this.restApiService.get(url);
  }

  getMessageBatches(ownerId: string): Promise<any> {
    const url = `messages/${ownerId}/get-batches`;
    return this.restApiService.get(url);
  }

  askQuestion(
    chatId: string,
    question: string,
    remark: string,
    name: string,
    userId: string,
    options?: any
  ): Promise<any> {
    const url = `messages/ask/${chatId}`;
    return this.restApiService.post(url, {
      question,
      remark,
      name,
      userId,
      options,
    });
  }

  findClients(ownerId: string, tags?: string[]): Promise<StudioClient[]> {
    let url = `clients/${ownerId}/find`;
    if (tags) {
      url += `?${queryString.stringify({ tags })}`;
    }
    return this.restApiService.get(url);
  }

  findClientsById(ownerId: string, ids: string[]): Promise<StudioClient[]> {
    const url = `clients/${ownerId}/find-clients-by-ids`;
    return this.restApiService.post(url, { ids });
  }

  sendMessage(ownerId: string, params: any): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .post(`messages/${ownerId}/send`, params)
      .then((result) => {
        ClientTracker.trackSendMessage({
          ownerId,
          params,
          success: true,
          duration: Date.now() - startTime,
          endpoint: 'sendMessage',
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackSendMessage({
          ownerId,
          params,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
          endpoint: 'sendMessage',
        });
        throw error;
      });
  }

  sendMessageInMessanger(ownerId: string, params: MessageParams): Promise<any> {
    const startTime = Date.now();
    let url: string;
    if (params.destinationType === 'client') {
      url = `clients/${ownerId}/send-in-messenger`;
    } else if (params.destinationType === 'pendingClient') {
      url = `pending-clients/${ownerId}/send-in-messenger`;
    } else {
      throw new Error('Invalid destination type');
    }

    return this.restApiService
      .post(url, params)
      .then((result) => {
        ClientTracker.trackSendMessage({
          ownerId,
          params,
          success: true,
          duration: Date.now() - startTime,
          endpoint: 'sendMessageInMessanger',
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackSendMessage({
          ownerId,
          params,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
          endpoint: 'sendMessageInMessanger',
        });
        throw error;
      });
  }

  sendEmails(ownerId: string, params: MessageParams): Promise<any> {
    const startTime = Date.now();
    let url: string;
    if (params.destinationType === 'client') {
      url = `clients/${ownerId}/send-email`;
    } else if (params.destinationType === 'pendingClient') {
      url = `pending-clients/${ownerId}/send-email`;
    } else {
      throw new Error('Invalid destination type');
    }

    return this.restApiService
      .post(url, params)
      .then((result) => {
        ClientTracker.trackSendMessage({
          ownerId,
          params,
          success: true,
          duration: Date.now() - startTime,
          endpoint: 'sendEmails',
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackSendMessage({
          ownerId,
          params,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
          endpoint: 'sendEmails',
        });
        throw error;
      });
  }

  getStudioClientInfo(ownerId: string, studioClientId: string): Promise<any> {
    const url = `studio-client-info/${ownerId}/${studioClientId}`;
    return this.restApiService.get(url);
  }

  addClientInfo(ownerId: string, studioClientId: string, data: ClientInfoData): Promise<any> {
    const startTime = Date.now();
    const url = `studio-client-info/${ownerId}/${studioClientId}`;

    return this.restApiService
      .post(url, data)
      .then((result) => {
        ClientTracker.trackAddClientInfo({
          ownerId,
          studioClientId,
          clientInfoData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackAddClientInfo({
          ownerId,
          studioClientId,
          clientInfoData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  updateClientInfo(ownerId: string, id: string, data: ClientInfoData): Promise<any> {
    const startTime = Date.now();
    const url = `studio-client-info/${ownerId}/${id}`;

    return this.restApiService
      .put(url, data)
      .then((result) => {
        ClientTracker.trackUpdateClientInfo({
          ownerId,
          id,
          clientInfoData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        ClientTracker.trackUpdateClientInfo({
          ownerId,
          id,
          clientInfoData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteClientInfo(ownerId: string, id: string): Promise<any> {
    const url = `studio-client-info/${ownerId}/${id}`;
    return this.restApiService.delete(url);
  }

  uploadDoc(
    ownerId: string,
    docUrl: string,
    mapping: SpreadsheetMapping,
    addAsTag: boolean,
    addAsClientInfo: boolean,
    commonFields: any
  ): Promise<any> {
    return this.restApiService.post(`clients/${ownerId}/upload-doc`, {
      url: docUrl,
      mapping: mapping,
      addAsTag: addAsTag,
      addAsClientInfo: addAsClientInfo,
      commonFields: commonFields,
    });
  }

  findClientUsersByStudioClientId(ownerId: string, studioClientId: string): Promise<any> {
    return this.restApiService.get(`clients/${ownerId}/${studioClientId}/find-client-users`);
  }

  connectPlatformUserWithStudioClient(ownerId: string, studioClientId: string, userId: string): Promise<any> {
    return this.restApiService.post(`clients/${ownerId}/connect/${studioClientId}/${userId}`);
  }
}

export default ClientsApiCall;
